import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaClock, FaList, FaCheck, FaTimes, FaSyncAlt } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../config/url";

const getToken = () =>
  localStorage.getItem("adminToken") ||
  localStorage.getItem("token") ||
  (() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
    } catch {
      return "";
    }
  })();

const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const Products = () => {
  const [counts, setCounts] = useState({
    totalProducts: 0,
    recentlyAddedProductsCount: 0,
    approvedProducts: 0,
    unapprovedProducts: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchCounts = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      setError("");

      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      // ✅ Try your backend counts endpoint first
      try {
        const res = await axios.get(`${baseurl}/api/product/counts`, {
          headers,
          withCredentials: true,
        });

        // supports multiple response shapes
        const data = res.data || {};
        const recentlyCount = Array.isArray(data.recentlyAddedProducts)
          ? data.recentlyAddedProducts.length
          : safeNum(data.recentlyAddedProductsCount);

        setCounts({
          totalProducts: safeNum(data.totalProducts),
          recentlyAddedProductsCount: recentlyCount,
          approvedProducts: safeNum(data.approvedProducts),
          unapprovedProducts: safeNum(data.unapprovedProducts),
        });

        return;
      } catch (e) {
        // ✅ fallback to /api/product if counts endpoint not working
      }

      // ✅ Fallback: fetch all products and compute counts in frontend
      const res2 = await axios.get(`${baseurl}/api/product`, {
        headers,
        withCredentials: true,
      });

      const list = Array.isArray(res2.data)
        ? res2.data
        : Array.isArray(res2.data?.products)
        ? res2.data.products
        : [];

      const totalProducts = list.length;
      const approvedProducts = list.filter((p) => p?.approved === true).length;
      const unapprovedProducts = list.filter((p) => p?.approved === false).length;

      // recently added = last 7 days (you can change to 3/15 days)
      const now = Date.now();
      const last7Days = 7 * 24 * 60 * 60 * 1000;
      const recentlyAddedProductsCount = list.filter((p) => {
        const d = new Date(p?.createdAt || 0).getTime();
        if (!Number.isFinite(d)) return false;
        return now - d <= last7Days;
      }).length;

      setCounts({
        totalProducts,
        recentlyAddedProductsCount,
        approvedProducts,
        unapprovedProducts,
      });
    } catch (err) {
      console.error("Error fetching product counts:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to load product stats.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCounts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = useMemo(
    () => [
      {
        title: "Total Products",
        value: counts.totalProducts,
        icon: <FaList className="text-2xl" />,
        iconBg: "bg-green-100 text-green-700",
        to: "/products/total",
        hint: "All products in catalog",
      },
      {
        title: "Recently Added",
        value: counts.recentlyAddedProductsCount,
        icon: <FaClock className="text-2xl" />,
        iconBg: "bg-blue-100 text-blue-700",
        to: "/products/new",
        hint: "Added in last 7 days",
      },
      {
        title: "Unapproved Products",
        value: counts.unapprovedProducts,
        icon: <FaTimes className="text-2xl" />,
        iconBg: "bg-red-100 text-red-700",
        to: "/products/not-approved",
        hint: "Needs approval action",
      },
      {
        title: "Approved Products",
        value: counts.approvedProducts,
        icon: <FaCheck className="text-2xl" />,
        iconBg: "bg-emerald-100 text-emerald-700",
        to: "/products/approved",
        hint: "Visible to buyers",
      },
    ],
    [counts]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading Product Management…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Product Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Quick overview of products with shortcuts to each section.
            </p>
          </div>

          <button
            onClick={() => fetchCounts(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 shadow-sm hover:bg-gray-50 disabled:opacity-60"
          >
            <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <Link
              key={c.title}
              to={c.to}
              className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{c.title}</div>
                  <div className="text-3xl font-bold text-gray-900 mt-2">{c.value}</div>
                  <div className="text-xs text-gray-500 mt-2">{c.hint}</div>
                </div>
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${c.iconBg}`}>
                  {c.icon}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Small Tip */}
        <div className="mt-5 text-xs text-gray-500">
          Note: “Recently Added” is calculated as products created in last <b>7 days</b> (frontend fallback).
        </div>
      </div>
    </div>
  );
};

export default Products;